import { track } from '@src/services/analytics';

// Shared, widget-agnostic tracking for the embeddable widgets funnel (viewed / step opened /
// step filled+edited / cta). Each widget binds its own name via createWidgetTracker so the same
// events can be reused across widgets (product-adder, cart, checkout, ...), distinguished by the
// `widget` property.

// State is keyed by `${widgetId}:${step}`. widgetId is a unique id per widget instance (Core.tsx),
// so keys never collide across instances or widget types.
const stepOpenedAt = new Map<string, number>();
const stepOpenedTracked = new Set<string>();
const stepFilledTracked = new Set<string>();

const stepKey = (widgetId: string, step: string) => `${widgetId}:${step}`;

export type WidgetEventProps = Record<string, unknown>;

export interface WidgetTracker {
  /** Widget was rendered. */
  viewed: (props?: WidgetEventProps) => void;
  /** A step was opened — emits widget_step_opened only on the first open of each step. */
  stepOpened: (widgetId: string, step: string, stepIndex: number, props?: WidgetEventProps) => void;
  /** A step was confirmed — first confirm emits widget_step_filled (with time_to_fill_ms), later confirms emit widget_step_edited. */
  stepConfirmed: (widgetId: string, step: string, props?: WidgetEventProps) => void;
  /** The widget CTA was clicked. */
  ctaClicked: (props?: WidgetEventProps) => void;
  /** Reset the fill timer for a step (e.g. when reopening) without emitting an event. */
  recordStepOpenTime: (widgetId: string, step: string) => void;
  /** Re-arm tracking on (re)mount. State is a shared singleton, so this clears every widget's entries. */
  reset: () => void;
}

export const createWidgetTracker = (widget: string): WidgetTracker => ({
  viewed: (props = {}) => track('widget_viewed', { ...props, widget }),

  stepOpened: (widgetId, step, stepIndex, props = {}) => {
    // Reset the fill timer on every open, but emit the event only the first time.
    const key = stepKey(widgetId, step);
    stepOpenedAt.set(key, Date.now());

    if (stepOpenedTracked.has(key)) {
      return;
    }
    stepOpenedTracked.add(key);

    track('widget_step_opened', { ...props, widget, step, step_index: stepIndex });
  },

  stepConfirmed: (widgetId, step, props = {}) => {
    const key = stepKey(widgetId, step);

    if (stepFilledTracked.has(key)) {
      track('widget_step_edited', { ...props, widget, step });
      return;
    }

    stepFilledTracked.add(key);
    const openedAt = stepOpenedAt.get(key);
    track('widget_step_filled', {
      ...props,
      widget,
      step,
      time_to_fill_ms: openedAt != null ? Date.now() - openedAt : undefined,
    });
  },

  ctaClicked: (props = {}) => track('widget_cta_clicked', { ...props, widget }),

  recordStepOpenTime: (widgetId, step) => {
    stepOpenedAt.set(stepKey(widgetId, step), Date.now());
  },

  reset: () => {
    stepOpenedTracked.clear();
    stepFilledTracked.clear();
    stepOpenedAt.clear();
  },
});
